import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, Link2, Users, MessageSquare } from 'lucide-react';

// Import existing managers
import FooterManager from './FooterManager';
import SocialMediaManager from './SocialMediaManager';
import AboutUsManager from './AboutUsManager';
import PrivacyPolicyManager from './PrivacyPolicyManager';
import TermsConditionsManager from './TermsConditionsManager';
import DisclaimerManager from './DisclaimerManager';

const PageSettingsManager = () => {
  const [activeTab, setActiveTab] = useState('footer');

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold">Page Settings</h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-2">
          Manage all your website pages, footer links, and static content from one place
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="!h-auto !inline-grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-6 w-full p-2">
          <TabsTrigger value="footer" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <Link2 className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">Footer Links</span>
            <span className="sm:hidden truncate">Footer</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <Users className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">Social Media</span>
            <span className="sm:hidden truncate">Social</span>
          </TabsTrigger>
          <TabsTrigger value="about" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">About Us</span>
            <span className="sm:hidden truncate">About</span>
          </TabsTrigger>
          <TabsTrigger value="privacy" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">Privacy Policy</span>
            <span className="sm:hidden truncate">Privacy</span>
          </TabsTrigger>
          <TabsTrigger value="terms" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <FileText className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">Terms & Conditions</span>
            <span className="sm:hidden truncate">Terms</span>
          </TabsTrigger>
          <TabsTrigger value="disclaimer" className="!w-full flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm px-2 py-3 min-h-[44px]">
            <MessageSquare className="w-4 h-4 flex-shrink-0" />
            <span className="hidden sm:inline truncate">Disclaimer</span>
            <span className="sm:hidden truncate">Disclaimer</span>
          </TabsTrigger>
        </TabsList>

        {/* Footer Links Tab */}
        <TabsContent value="footer" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">Footer Links Management</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Manage footer sections including Follow Us, Support, Join the Community, Let's Get Social, and Collaboration
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <FooterManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">Social Media Links</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Manage your social media profiles (Facebook, Twitter, Instagram, LinkedIn, YouTube)
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <SocialMediaManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* About Us Tab */}
        <TabsContent value="about" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">About Us Page</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Edit your About Us page content
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <AboutUsManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Privacy Policy Tab */}
        <TabsContent value="privacy" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">Privacy Policy</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Manage your privacy policy content
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <PrivacyPolicyManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Terms & Conditions Tab */}
        <TabsContent value="terms" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">Terms & Conditions</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Manage your terms and conditions content
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <TermsConditionsManager />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Disclaimer Tab */}
        <TabsContent value="disclaimer" className="mt-0">
          <Card className="w-full overflow-hidden">
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-lg sm:text-xl break-words">Disclaimer</CardTitle>
              <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 break-words">
                Manage your disclaimer and disclosure statement
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 overflow-x-auto">
              <DisclaimerManager />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PageSettingsManager;
